import React, { useState, useEffect } from 'react';
import AdminPasswordModal from './AdminPasswordModal';

const withAdminPassword = (WrappedComponent, options = {}) => {
  const { 
    title = "Admin Verification Required",
    sessionTimeout = 30 * 60 * 1000 // 30 minutes in milliseconds
  } = options;

  return function ProtectedAdminComponent(props) {
    const [isVerified, setIsVerified] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      checkVerification();
    }, []);

    const checkVerification = () => {
      const verified = sessionStorage.getItem('adminVerified');
      const verifiedTime = sessionStorage.getItem('adminVerifiedTime');
      
      if (verified === 'true' && verifiedTime) {
        const timeElapsed = Date.now() - parseInt(verifiedTime);
        
        if (timeElapsed < sessionTimeout) {
          setIsVerified(true);
          setIsLoading(false);
          return;
        } else {
          // Session expired, clear verification
          sessionStorage.removeItem('adminVerified');
          sessionStorage.removeItem('adminVerifiedTime');
        }
      }
      
      setIsVerified(false);
      setIsLoading(false);
      setShowModal(true);
    };

    const handleVerificationSuccess = () => {
      setIsVerified(true);
      setShowModal(false);
    };

    const handleModalClose = () => {
      setShowModal(false);
      // If user closes modal without verifying, they can't access the component
    };

    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!isVerified) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h3>
            <p className="text-gray-500 mb-4">This section requires additional verification.</p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-6 6c-3 0-5.5-1.5-5.5-4a3.5 3.5 0 117 0c0 2.5-2.5 4-5.5 4a6 6 0 01-6-6 2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Verify Access
            </button>
          </div>
          
          <AdminPasswordModal
            isOpen={showModal}
            onClose={handleModalClose}
            onSuccess={handleVerificationSuccess}
            title={title}
          />
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

export default withAdminPassword;
